import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle, Package, Home, ShoppingBag} from "lucide-react";

export default function ThankYou() {
  const location = useLocation();
  const navigate = useNavigate();
  const [count,  setCount]  = useState(5);

  // ── Données passées depuis la page Checkout ──────────
  const order = location.state?.order || null;

  // ── Redirection auto après 5s ────────────────────────
  useEffect(() => {
    const timer = setInterval(() => {
      setCount(c => {
        if (c <= 1) { clearInterval(timer); navigate("/"); }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [navigate]);

  return (
    <div style={{ background: "#f8f9ff", minHeight: "100vh",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  padding: "2rem" }}>
      <div style={{ maxWidth: 520, width: "100%", textAlign: "center" }}>

        {/* ── Icône succès animée ── */}
        <div style={{
          width: 96, height: 96, borderRadius: "50%",
          background: "rgba(34,197,94,0.1)",
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 1.5rem",
          animation: "pop .4s ease",
        }}>
          <CheckCircle size={52} color="#22c55e" strokeWidth={1.5} />
        </div>

        <style>{`
          @keyframes pop {
            0%   { transform: scale(0); opacity: 0; }
            70%  { transform: scale(1.1); }
            100% { transform: scale(1); opacity: 1; }
          }
        `}</style>

        {/* ── Titre ── */}
        <h1 className="fw-bold mb-2" style={{ fontSize: 28 }}>
          Merci pour votre commande ! 🎉
        </h1>
        <p className="text-muted mb-4">
          Votre commande a été confirmée et sera traitée dans les plus brefs délais.
        </p>

        {/* ── Carte résumé commande ── */}
        {order && (
          <div className="rounded-4 p-4 mb-4 text-start"
            style={{ background: "#fff", border: "1px solid #eee",
                     boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>

            <div className="d-flex align-items-center gap-2 mb-3">
              <Package size={18} color="#6c63ff" />
              <span className="fw-bold">Détails de la commande</span>
            </div>

            {/* Numéro commande */}
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted small">Numéro</span>
              <span className="fw-medium small" style={{ color: "#6c63ff" }}>
                #{order.id?.slice(0, 8).toUpperCase()}
              </span>
            </div>

            {/* Articles */}
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted small">Articles</span>
              <span className="fw-medium small">{order.items?.length || 0} article(s)</span>
            </div>

            {/* Total */}
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted small">Total</span>
              <span className="fw-bold" style={{ color: "#6c63ff" }}>
                {(order.total || 0).toFixed(2)} MAD
              </span>
            </div>

            {/* Livraison */}
            <div className="d-flex justify-content-between">
              <span className="text-muted small">Livraison</span>
              <span className="fw-medium small">{order.shipping?.city || "—"}</span>
            </div>

            {/* Liste produits */}
            {order.items?.length > 0 && (
              <>
                <hr className="my-3" />
                <div className="d-flex flex-column gap-2">
                  {order.items.map((item, i) => (
                    <div key={i} className="d-flex align-items-center gap-2">
                      <img
                        src={item.image || "https://via.placeholder.com/36"}
                        alt={item.name}
                        style={{ width: 36, height: 36, borderRadius: 6, objectFit: "cover" }}
                        onError={e => e.target.src = "https://via.placeholder.com/36"}
                      />
                      <span className="small flex-grow-1 text-truncate">{item.name}</span>
                      <span className="small fw-medium">x{item.quantity}</span>
                      <span className="small fw-bold" style={{ color: "#6c63ff" }}>
                        {((item.price || 0) * (item.quantity || 1)).toFixed(2)} MAD
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Étapes livraison ── */}
        <div className="rounded-4 p-4 mb-4"
          style={{ background: "#fff", border: "1px solid #eee" }}>
          <div className="d-flex justify-content-between align-items-center position-relative">

            {/* Ligne de progression */}
            <div style={{
              position: "absolute", top: "50%", left: "10%", right: "10%",
              height: 2, background: "#e5e7eb", transform: "translateY(-50%)", zIndex: 0
            }} />
            <div style={{
              position: "absolute", top: "50%", left: "10%", width: "33%",
              height: 2, background: "#6c63ff", transform: "translateY(-50%)", zIndex: 1,
              transition: "width .5s"
            }} />

            {[
              { label: "Confirmée", active: true  },
              { label: "En cours",  active: false },
              { label: "Livrée",    active: false },
            ].map((step, i) => (
              <div key={i} className="d-flex flex-column align-items-center gap-1"
                style={{ zIndex: 2 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: "50%",
                  background: step.active ? "#6c63ff" : "#e5e7eb",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: step.active ? "#fff" : "#9ca3af",
                  fontWeight: 700, fontSize: 13,
                }}>
                  {i + 1}
                </div>
                <span style={{ fontSize: 11, color: step.active ? "#6c63ff" : "#9ca3af",
                               fontWeight: step.active ? 600 : 400 }}>
                  {step.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Boutons ── */}
        <div className="d-flex gap-3 justify-content-center flex-wrap">
          <Link to="/"
            className="btn fw-bold rounded-3 d-flex align-items-center gap-2 text-white"
            style={{ background: "#6c63ff" }}>
            <Home size={15} /> Retour à l'accueil
          </Link>
          <Link to="/category/AllProducts"
            className="btn btn-outline-secondary fw-bold rounded-3 d-flex align-items-center gap-2">
            <ShoppingBag size={15} /> Continuer mes achats
          </Link>
        </div>

        {/* ── Redirection auto ── */}
        <p className="text-muted small mt-4">
          Redirection automatique dans <strong>{count}s</strong>...
        </p>

      </div>
    </div>
  );
}